import React from 'react';
import { AppBar, Toolbar, IconButton, Box, Button } from '@mui/material';
import {
  ArrowBack,
  Settings as SettingsIcon,
  Search,
  MoreVert,
  LocalFlorist,
  Info,
  Person
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useSafeArea } from '../hooks/useSafeArea';

interface TopBarProps {
  title?: string;
  icon?: React.ReactNode;
  showBackButton?: boolean;
  showSettingsButton?: boolean;
  showSearchButton?: boolean;
  showMoreButton?: boolean;
  showFloralButton?: boolean;
  showInfoButton?: boolean;
  showProfileButton?: boolean;
  rightActions?: React.ReactNode;
  onBackClick?: () => void;
  onSettingsClick?: () => void;
  onSearchClick?: () => void;
  onMoreClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onFloralClick?: () => void;
  onInfoClick?: () => void;
  onProfileClick?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  title = 'Hostly',
  icon,
  showBackButton = false,
  showSettingsButton = false,
  showSearchButton = false,
  showMoreButton = false,
  showFloralButton = false,
  showInfoButton = false,
  showProfileButton = false,
  rightActions,
  onBackClick,
  onSettingsClick,
  onSearchClick,
  onMoreClick,
  onFloralClick,
  onInfoClick,
  onProfileClick,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { top } = useSafeArea();

  const handleBackClick = (): void => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleProfileClick = (): void => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      navigate('/settings');
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: theme.colors.background,
        borderBottom: `1px solid ${theme.colors.border}`,
        top: 0,
        paddingTop: `${top}px`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, gap: 1, padding: '8px 16px' }}>
        {/* Left side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showBackButton && (
            <IconButton 
              edge="start" 
              onClick={handleBackClick}
              sx={{ color: theme.colors.text.primary }}
            >
              <ArrowBack />
            </IconButton>
          )}
          
          {showSettingsButton ? (
            <Button
              onClick={onSettingsClick}
              startIcon={<SettingsIcon sx={{ fontSize: '0.875rem' }} />}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                borderRadius: theme.radii.medium,
                padding: '8px 20px',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Configurações
            </Button>
          ) : (
            <Button
              startIcon={icon}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                borderRadius: theme.radii.medium,
                padding: '8px 20px',
                fontSize: '0.875rem',
                fontWeight: 'normal',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              {title}
            </Button>
          )}
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto', zIndex: 10, position: 'relative' }}>
          {showProfileButton && (
            <IconButton
              onClick={handleProfileClick}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                margin: '0 4px',
                zIndex: 10,
                pointerEvents: 'auto',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <Person />
            </IconButton>
          )}

          {showInfoButton && (
            <IconButton
              onClick={onInfoClick}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                margin: '0 4px',
                zIndex: 10,
                pointerEvents: 'auto',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <Info />
            </IconButton>
          )}

          {showFloralButton && (
            <IconButton
              onClick={onFloralClick}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                margin: '0 4px',
                zIndex: 10,
                pointerEvents: 'auto',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <LocalFlorist />
            </IconButton>
          )}

          {showSearchButton && (
            <IconButton
              onClick={onSearchClick}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                margin: '0 4px',
                zIndex: 10,
                pointerEvents: 'auto',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <Search />
            </IconButton>
          )}

          {showMoreButton && (
            <IconButton
              onClick={(e) => onMoreClick?.(e)}
              sx={{
                color: theme.colors.text.primary,
                zIndex: 10,
                pointerEvents: 'auto',
              }}
            >
              <MoreVert />
            </IconButton>
          )}

          {rightActions}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
